import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Book } from "@/types/book";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ChevronLeft, ChevronRight, BookmarkPlus, Check } from "lucide-react";
import { useAppConfig } from "@/hooks/use-app-config";
import { useSubscriptionStatus } from "@/hooks/use-subscription-status";
import { useAuth } from "@/components/AuthProvider";
import { SubscriptionPurchaseButton } from '@/components/SubscriptionPurchaseButton';
import { getInstantHeroes, setCachedHeroes, isCacheStale, type CachedHero } from '@/lib/heroCache';
import { SLIDE_DURATION_FIRST, SLIDE_DURATION_DEFAULT } from '@/lib/heroConfig';

type Hero = {
  title: string;
  media_url: string;
  media_type: 'image' | 'video';
  subtitle: string | null;
  logged_out_subtitle: string | null;
  subscribed_subtitle: string | null;
  expired_subtitle: string | null;
  logged_out_title: string | null;
  subscribed_title: string | null;
  expired_title: string | null;
  book_id: number | null;
  tag: string | null;
  hide_logo: boolean;
  title_font: string | null;
};

type HeroFromDB = {
  title: string;
  media_url: string;
  media_type: string;
  subtitle: string | null;
  logged_out_subtitle: string | null;
  subscribed_subtitle: string | null;
  expired_subtitle: string | null;
  logged_out_title: string | null;
  subscribed_title: string | null;
  expired_title: string | null;
  id: number;
  position: number;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
  book_id: number | null;
  tag: string | null;
  hide_logo: boolean;
  title_font: string | null;
};

// Define cache keys
const HEROES_QUERY_KEY = 'heroes';
const PREVIEW_BOOK_QUERY_KEY = 'preview-book';

interface HeroSectionProps {
  onBookSelect?: (book: Book) => void;
  backgroundOnly?: boolean;
  onOpenAuthModal?: () => void;
  onReadingListToggle?: (bookId: number) => void;
  isInReadingList?: (bookId: number) => boolean;
  isReadingListLoading?: boolean;
}

export function HeroSection({ onBookSelect, backgroundOnly = false, onOpenAuthModal, onReadingListToggle, isInReadingList, isReadingListLoading }: HeroSectionProps) {
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [progress, setProgress] = useState(0);
  const [allImagesPreloaded, setAllImagesPreloaded] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get app configuration and subscription status
  const { data: appConfig } = useAppConfig();
  const { data: subscriptionStatus, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const { session } = useAuth();
  
  // Determine if user has actual subscription access (even if cancelled but period hasn't ended)
  const hasSubscriptionAccess = useMemo(() => {
    if (!subscriptionStatus) return false;
    if (subscriptionStatus.subscription_status === 'active') return true;
    // Cancelled but still has time left
    if (subscriptionStatus.subscription_status === 'cancelled' && subscriptionStatus.current_period_end) {
      const periodEnd = new Date(subscriptionStatus.current_period_end);
      return periodEnd > new Date();
    }
    return false;
  }, [subscriptionStatus]);

  // Embla Carousel setup with autoplay - start paused until images preloaded
  const autoplayPlugin = useRef(
    Autoplay({ 
      delay: SLIDE_DURATION_DEFAULT, 
      stopOnInteraction: false, 
      stopOnMouseEnter: false,
      playOnInit: false // Don't autoplay until images are ready
    })
  );
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      duration: 25,
      dragFree: false,
      containScroll: 'trimSnaps',
      dragThreshold: 12,
    },
    [autoplayPlugin.current]
  );

  // Get instant cached heroes for immediate render (sync, no network)
  const instantHeroes = useMemo(() => getInstantHeroes(), []);

  // Fetch heroes with React Query for proper caching
  const { data: heroes = instantHeroes, isLoading: isHeroesLoading } = useQuery({
    queryKey: [HEROES_QUERY_KEY],
    queryFn: async () => {
      try {
        const { data: heroesData, error } = await supabase
          .from('heroes')
          .select('title, media_url, media_type, subtitle, logged_out_subtitle, subscribed_subtitle, expired_subtitle, logged_out_title, subscribed_title, expired_title, book_id, tag, hide_logo, title_font, position')
          .eq('is_active', true)
          .order('position');
        
        if (error) {
          console.error('Error loading heroes:', error);
          return instantHeroes;
        }
        
        if (!heroesData?.length) return instantHeroes;
        
        // Simplified: all heroes are images, use media_url directly
        const validHeroes: Hero[] = heroesData.map((hero) => ({
          title: hero.title,
          media_url: hero.media_url,
          media_type: 'image' as const,
          subtitle: hero.subtitle,
          logged_out_subtitle: hero.logged_out_subtitle,
          subscribed_subtitle: hero.subscribed_subtitle,
          expired_subtitle: hero.expired_subtitle,
          logged_out_title: hero.logged_out_title,
          subscribed_title: hero.subscribed_title,
          expired_title: hero.expired_title,
          book_id: hero.book_id,
          tag: hero.tag,
          hide_logo: hero.hide_logo ?? false,
          title_font: hero.title_font
        }));
        
        // Cache the fresh data for next load
        if (validHeroes.length > 0) {
          setCachedHeroes(validHeroes as CachedHero[]);
        }
        
        return validHeroes;
      } catch (error) {
        console.error('Error in heroes query function:', error);
        return instantHeroes;
      }
    },
    initialData: instantHeroes,
    staleTime: isCacheStale() ? 0 : 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: isCacheStale(),
    refetchOnReconnect: false
  });

  // Preload ALL hero images before enabling carousel autoplay
  useEffect(() => {
    if (heroes.length === 0) return;
    
    const imageUrls = heroes
      .map(h => h.media_url)
      .filter(Boolean) as string[];
    
    if (imageUrls.length === 0) {
      setAllImagesPreloaded(true);
      return;
    }
    
    // Preload all images in parallel
    const preloadPromises = imageUrls.map(url => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Don't block on errors
        img.src = url;
      });
    });
    
    Promise.all(preloadPromises).then(() => {
      setAllImagesPreloaded(true);
      // Only start autoplay after all images are preloaded
      // Defensive: ensure embla is fully initialized with valid snap list before playing
      if (emblaApi && emblaApi.scrollSnapList && emblaApi.scrollSnapList().length > 0) {
        autoplayPlugin.current.play();
      }
    });
  }, [heroes, emblaApi]);

  // Check book status (new release or preview) and fetch book tags
  const { data: bookStatus } = useQuery({
    queryKey: ['hero-book-status', heroes[currentHeroIndex]?.book_id],
    queryFn: async () => {
      const currentHero = heroes[currentHeroIndex];
      if (!currentHero?.book_id) return { isNew: false, isFree: false, tags: [] };
      
      const { data, error } = await supabase
        .from('books')
        .select('created_at, is_preview, tags')
        .eq('id', currentHero.book_id)
        .maybeSingle();
      
      if (error || !data) return { isNew: false, isFree: false, tags: [] };
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return {
        isNew: new Date(data.created_at) > thirtyDaysAgo,
        isFree: data.is_preview === true,
        tags: data.tags || []
      };
    },
    enabled: !!heroes[currentHeroIndex]?.book_id && heroes.length > 0,
    staleTime: 5 * 60 * 1000
  });

  // Fetch preview book with React Query for proper caching
  const { data: previewBook, isLoading: isPreviewBookLoading } = useQuery({
    queryKey: [PREVIEW_BOOK_QUERY_KEY],
    queryFn: async () => {
      try {
        const { data: booksData, error } = await supabase
          .from('books')
          .select(`
            id,
            title,
            author,
            description,
            short_description,
            cover_image_url,
            file_path,
            is_preview,
            created_at,
            updated_at,
            price,
            bundle_id,
            bundle_price,
            is_featured
          `)
          .eq('is_preview', true)
          .limit(1);

        if (error) {
          throw error;
        }

        const book = booksData?.[0];
        if (!book) return null;

        return {
          ...book,
          id: book.id,
          title: book.title,
          author: book.author,
          description: book.description || null,
          short_description: book.short_description || null,
          cover_image_url: book.cover_image_url || null,
          file_path: book.file_path || null,
          created_at: book.created_at || null,
          updated_at: book.updated_at || null,
          price: book.price ?? 0,
          bundle_id: book.bundle_id ?? null,
          bundle_price: book.bundle_price ?? null,
          is_featured: book.is_featured ?? false
        };
      } catch (error) {
        console.error('Error loading preview book:', error);
        toast({
          title: "Error",
          description: "An unexpected error occurred while loading the preview book.",
          variant: "destructive"
        });
        return null;
      }
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });

  const handleReadNow = useCallback(async () => {
    const currentHero = heroes[currentHeroIndex];
    
    // Check authentication FIRST - always require login
    const { data: { session: currentSession } } = await supabase.auth.getSession();
    
    if (!currentSession?.user) {
      onOpenAuthModal?.();
      return;
    }
    
    // If this is a free/preview book, navigate directly to reader
    if (bookStatus?.isFree && currentHero?.book_id) {
      navigate(`/read/${currentHero.book_id}`);
      return;
    }
    
    // Check subscription if needed
    if (appConfig?.current_model === 'subscription' && subscriptionStatus?.subscription_status !== 'active') {
      return;
    }
    
    // If hero has a book_id, fetch that book and open dialog
    if (currentHero?.book_id) {
      try {
        const { data: bookData, error } = await supabase
          .from('books')
          .select(`
            id,
            title,
            author,
            description,
            short_description,
            cover_image_url,
            file_path,
            is_preview,
            created_at,
            updated_at,
            price,
            bundle_id,
            bundle_price,
            is_featured
          `)
          .eq('id', currentHero.book_id)
          .maybeSingle();

        if (error || !bookData) {
          toast({
            title: "Book not found",
            description: "The book for this hero could not be found.",
            variant: "destructive"
          });
          return;
        }

        const book: Book = {
          ...bookData,
          id: bookData.id,
          title: bookData.title,
          author: bookData.author,
          description: bookData.description || null,
          short_description: bookData.short_description || null,
          cover_image_url: bookData.cover_image_url || null,
          file_path: bookData.file_path || null,
          created_at: bookData.created_at || null,
          updated_at: bookData.updated_at || null,
          price: bookData.price ?? 0,
          bundle_id: bookData.bundle_id ?? null,
          bundle_price: bookData.bundle_price ?? null,
          is_featured: bookData.is_featured ?? false
        };

        if (onBookSelect) {
          onBookSelect(book);
        } else {
          navigate(`/read/${book.id}`);
        }
      } catch (error) {
        console.error('Error fetching book:', error);
        toast({
          title: "Error",
          description: "Failed to load book details.",
          variant: "destructive"
        });
      }
    } else if (previewBook) {
      if (onBookSelect) {
        onBookSelect(previewBook);
      } else {
        navigate(`/read/${previewBook.id}`);
      }
    } else {
      toast({
        title: "Preview not available",
        description: "The preview book is not available at the moment. Please try again later.",
        variant: "destructive"
      });
    }
  }, [currentHeroIndex, heroes, previewBook, navigate, toast, onBookSelect, onOpenAuthModal, appConfig, subscriptionStatus, bookStatus]);

  // Navigation controls - defensive checks
  const scrollPrev = useCallback(() => {
    if (emblaApi && emblaApi.canScrollPrev && emblaApi.canScrollPrev()) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);
  
  const scrollNext = useCallback(() => {
    if (emblaApi && emblaApi.canScrollNext && emblaApi.canScrollNext()) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);
  
  const scrollTo = useCallback((index: number) => {
    if (emblaApi && emblaApi.scrollTo && typeof index === 'number' && index >= 0) {
      emblaApi.scrollTo(index);
    }
  }, [emblaApi]);

  // Parallax scroll effect - throttled with requestAnimationFrame
  useEffect(() => {
    let rafId: number | null = null;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // Sync current hero index with Embla and dynamically set autoplay delay
  // Defensive: check emblaApi exists and has required methods
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      // Defensive: check that scrollSnap methods exist and return valid data
      if (!emblaApi.scrollSnapList || !emblaApi.selectedScrollSnap) return;
      
      const snapList = emblaApi.scrollSnapList();
      if (!snapList || snapList.length === 0) return;
      
      const currentIndex = emblaApi.selectedScrollSnap();
      if (typeof currentIndex !== 'number' || currentIndex < 0) return;
      
      setCurrentHeroIndex(currentIndex);
      setProgress(0);
      
      // Dynamically set autoplay delay based on slide index
      const newDelay = currentIndex === 0 ? SLIDE_DURATION_FIRST : SLIDE_DURATION_DEFAULT;
      autoplayPlugin.current.reset();
      // Update the autoplay options with new delay
      (autoplayPlugin.current as any).options.delay = newDelay;
    };

    emblaApi.on('select', onSelect);
    onSelect();

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi]);

  // Simple progress bar for static images - ONLY handles progress UI, not slide advancement
  // Embla autoplay handles all slide transitions to prevent race conditions
  useEffect(() => {
    if (!emblaApi || heroes.length === 0) return;

    // Clear any existing timer
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    const slideDuration = currentHeroIndex === 0 ? SLIDE_DURATION_FIRST : SLIDE_DURATION_DEFAULT;
    const intervalMs = 50;
    const increment = (100 / slideDuration) * intervalMs;

    setProgress(0);
    
    // Only start progress if images are preloaded
    if (!allImagesPreloaded) return;
    
    // Let Embla autoplay handle transitions - we only animate the progress bar
    // Defensive: ensure embla is fully initialized with valid snap list before playing
    if (emblaApi.scrollSnapList && emblaApi.scrollSnapList().length > 0) {
      autoplayPlugin.current.play();
    }

    progressIntervalRef.current = setInterval(() => {
      setProgress((prev) => Math.min(100, prev + increment));
    }, intervalMs);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [emblaApi, currentHeroIndex, heroes.length, allImagesPreloaded]);

  if (!heroes || heroes.length === 0) {
    return null;
  }

  return (
    <section 
      className={`relative w-full overflow-hidden ${backgroundOnly ? 'h-screen' : 'mt-0 mb-4'}`} 
      style={{ 
        pointerEvents: backgroundOnly ? 'none' : 'auto',
        zIndex: 10,
        marginTop: 'calc(-1 * env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <div 
        ref={emblaRef}
        className={`embla hero-container w-full ${backgroundOnly ? 'h-screen' : 'h-[80vh] md:h-[clamp(520px,70vh,760px)]'} mx-auto relative overflow-hidden`} 
        style={{ background: 'hsl(var(--background))', height: backgroundOnly ? '100vh' : undefined }}
      >
        <div className="embla__container flex h-full items-stretch">
          {heroes.map((hero, index) => (
            <div
              key={index}
              className="embla__slide relative flex-[0_0_100%] min-w-0 h-full"
              style={{
                // Only promote active/adjacent slides to GPU layer
                contain: 'layout',
                ...(Math.abs(index - currentHeroIndex) <= 1 || Math.abs(index - currentHeroIndex) === heroes.length - 1
                  ? { willChange: 'transform, opacity' }
                  : {})
              }}
            >
              {/* Background layer with iOS overscroll stretch */}
              <div 
                className="absolute inset-0"
                style={{
                  transform: `scale(calc(1 + var(--overscroll, 0px) / 1000))`,
                  transition: 'transform 0.1s ease-out',
                  willChange: 'transform'
                }}
              >
                {/* Static image - ALWAYS render all slides for instant transitions */}
                {/* Loop-aware opacity: show current, adjacent, AND wrap-around slides */}
                <img
                  src={hero.media_url}
                  alt={hero.title}
                  className="absolute inset-0 w-full h-full object-cover gpu-stable"
                  loading="eager"
                  {...({ fetchpriority: index === 0 ? "high" : "auto" } as any)}
                  decoding={index === 0 ? "sync" : "async"}
                  style={{
                    // Loop-aware visibility: adjacent slides OR wrap-around (last<->first)
                    opacity: (() => {
                      const diff = Math.abs(index - currentHeroIndex);
                      const isAdjacent = diff <= 1;
                      const isWrapAround = diff === heroes.length - 1;
                      return isAdjacent || isWrapAround ? 1 : 0;
                    })(),
                    backfaceVisibility: 'hidden',
                    transform: 'translateZ(0)'
                  }}
                />
              </div>

              {/* Content - moves with slide using Framer Motion */}
              {/* mode="sync" allows enter/exit to overlap, preventing gaps when spamming nav */}
              {!backgroundOnly && (
                <AnimatePresence mode="sync">
                  {index === currentHeroIndex && (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ 
                        duration: 0.2, 
                        ease: [0.22, 1, 0.36, 1]
                      }}
                      className="absolute bottom-0 left-0 right-0 pb-[4rem] text-white z-30 flex flex-col items-center md:items-start justify-end"
                    >
                      {/* Dark gradient background for content - solid for better performance */}
                      <motion.div 
                        key={`glass-${currentHeroIndex}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className="absolute bottom-0 left-0 right-0 h-full z-20 bg-gradient-to-t from-black via-black/80 to-transparent" 
                        style={{ 
                          maskImage: 'linear-gradient(to top, black 0%, black 70%, transparent 100%)'
                        }}
                      />
                      
                      <div className="space-y-1 w-[90vw] md:w-full max-w-[90vw] md:max-w-[45vw] relative z-30 px-4 md:px-12 xl:px-16">
                        {(hero.tag || bookStatus?.isNew || bookStatus?.isFree) && (
                          <div className="flex justify-center md:justify-start mb-2">
                            <span className="px-3 py-1 bg-black/90 rounded-xl text-white text-xs font-apple font-medium border border-white/20">
                              {hero.tag || (bookStatus?.isFree ? 'Free' : 'New')}
                            </span>
                          </div>
                        )}
                        <h2
                          className={`${(() => {
                            const parts = hero.title_font?.split('|') || [];
                            const sizeClass = parts[1] || '';
                            const weightClass = parts[2] || '';
                            const size = sizeClass || 'text-5xl sm:text-6xl md:text-6xl lg:text-7xl xl:text-8xl';
                            const weight = weightClass || 'font-bold';
                            return `${size} font-display ${weight} tracking-tight text-center md:text-left leading-tight ${sizeClass ? '' : 'whitespace-nowrap'}`;
                          })()}`}
                          style={{
                            fontSize: (() => {
                              const parts = hero.title_font?.split('|') || [];
                              const sc = parts[1];
                              if (!sc) return 'clamp(2rem, 12vw, 6rem)';
                              const m = /^text-\[(.+)\]$/.exec(sc);
                              if (m) {
                                const val = m[1];
                                if (/^[0-9a-zA-Z.%\-\s(),]+$/.test(val)) return val;
                              }
                              return undefined;
                            })(),
                            fontFamily: (() => {
                              const parts = hero.title_font?.split('|') || [];
                              const fontFamily = parts[0];
                              const fontMap: Record<string, string> = {
                                'sf-pro': '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                                'apple': '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", Helvetica Neue, sans-serif',
                                'display': undefined,
                                'sans': undefined,
                              };
                              return fontFamily && fontMap[fontFamily] !== undefined ? fontMap[fontFamily] : undefined;
                            })()
                          }}
                        >
                          {(() => {
                            // If logged in but subscription status still loading, show subscribed text to avoid flash
                            if (session?.user && isSubscriptionLoading) {
                              const userName = session.user.user_metadata?.first_name || session.user.user_metadata?.full_name || 'there';
                              return (hero.subscribed_title || hero.title).replace('{name}', userName);
                            }
                            
                            const status = subscriptionStatus?.subscription_status;
                            const hadSub = subscriptionStatus?.has_had_subscription;
                            if (!session?.user) {
                              return hero.logged_out_title || hero.title;
                            } else if (status === 'active') {
                              const userName = session.user.user_metadata?.first_name || session.user.user_metadata?.full_name || 'there';
                              return (hero.subscribed_title || hero.title).replace('{name}', userName);
                            } else if (status === 'cancelled' || status === 'past_due' || (status === 'inactive' && hadSub)) {
                              return hero.expired_title || hero.title;
                            } else {
                              return hero.logged_out_title || hero.title;
                            }
                           })()}
                        </h2>
                        {(() => {
                          const getSubtitle = () => {
                            // If logged in but subscription status still loading, show subscribed subtitle to avoid flash
                            if (session?.user && isSubscriptionLoading) {
                              return hero.subscribed_subtitle || hero.subtitle;
                            }
                            
                            const status = subscriptionStatus?.subscription_status;
                            const hadSub = subscriptionStatus?.has_had_subscription;
                            if (!session) {
                              return hero.logged_out_subtitle || hero.subtitle;
                            } else if (status === 'active') {
                              return hero.subscribed_subtitle || hero.subtitle;
                            } else if (status === 'cancelled' || status === 'past_due' || (status === 'inactive' && hadSub)) {
                              return hero.expired_subtitle || hero.subtitle;
                            } else {
                              return hero.logged_out_subtitle || hero.subtitle;
                            }
                          };
                          
                          const displaySubtitle = getSubtitle();
                          
                          if (!hero.hide_logo && displaySubtitle) {
                            const hasMiniTag = bookStatus?.tags?.some((tag: string) => tag.toLowerCase() === 'mini');
                            
                            return (
                              <p className="md:text-[13px] font-apple font-semibold opacity-100 md:text-left text-white text-center text-xs flex items-center gap-2 justify-center md:justify-start">
                                <span className="w-5 h-5 bg-black rounded-full flex items-center justify-center p-1">
                                  <img src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/buxbox_tiny.webp" alt="" className="w-full h-full" />
                                </span>
                                {displaySubtitle}
                                {!hasMiniTag && (
                                  <img src="https://hwjejrsktuenskryvsvl.supabase.co/storage/v1/object/public/applogos/Icons%20custom/pg_tiny.webp" alt="PG" className="w-5 h-5" />
                                )}
                              </p>
                            );
                          } else if (hero.hide_logo && displaySubtitle) {
                            return (
                              <p className="md:text-[13px] font-apple font-semibold md:text-left text-[#FFFFFFB2] text-center text-xs justify-center md:justify-start">
                                {displaySubtitle}
                              </p>
                            );
                          }
                          return null;
                        })()}
                        {/* Button container - only add min-height when there's actually a button */}
                        {(hero.book_id || (appConfig?.current_model === 'subscription' && !hasSubscriptionAccess)) && (
                        <div className="flex flex-col items-center md:items-start pt-1 md:pt-2">
                          <div className="flex gap-2 items-center justify-center md:justify-start">
                            {hero.book_id ? (
                              bookStatus?.isFree || !(appConfig?.current_model === 'subscription' && !hasSubscriptionAccess) ? (
                                <div className="flex flex-col items-center">
                                  <Button 
                                    onClick={handleReadNow} 
                                    disabled={loading}
                                    size="lg" 
                                    className="bg-white text-black hover:bg-white/90 font-semibold h-11 md:h-14 px-6 min-w-[160px] md:min-w-[180px] transition-all duration-200 flex items-center justify-center gap-2 my-[7px] disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_2px_6px_rgba(0,0,0,0.08)] text-[15px] md:text-[17px] tracking-[-0.3px] rounded-xl"
                                    style={{
                                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, sans-serif'
                                    }}
                                  >
                                    {loading ? (
                                      <Loader2 className="w-5 h-5 animate-spin" />
                                    ) : (
                                      'Read Now'
                                    )}
                                  </Button>
                                </div>
                              ) : (
                                <SubscriptionPurchaseButton 
                                  onReadingListToggle={hero.book_id ? () => onReadingListToggle?.(hero.book_id) : undefined}
                                  isInReadingList={hero.book_id ? isInReadingList?.(hero.book_id) : false}
                                  isReadingListLoading={isReadingListLoading}
                                  onOpenAuthModal={onOpenAuthModal}
                                />
                              )
                            ) : (
                              appConfig?.current_model === 'subscription' && !hasSubscriptionAccess ? (
                                <SubscriptionPurchaseButton onOpenAuthModal={onOpenAuthModal} />
                              ) : null
                            )}

                            {onReadingListToggle && session && hero.book_id != null && hero.book_id !== 0 && (bookStatus?.isFree || !(appConfig?.current_model === 'subscription' && !hasSubscriptionAccess)) && (
                              <Button
                                onClick={() => onReadingListToggle(hero.book_id!)}
                                disabled={isReadingListLoading}
                                className="h-11 w-11 md:h-14 md:w-14 bg-black/60 text-white hover:bg-black/80 border border-white/20 font-semibold rounded-full transition-all duration-200 flex items-center justify-center flex-shrink-0 shadow-[0_2px_6px_rgba(0,0,0,0.08)]"
                              >
                                {isReadingListLoading ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                                ) : isInReadingList?.(hero.book_id!) ? (
                                  <Check className="w-5 h-5" />
                                ) : (
                                  <BookmarkPlus className="w-5 h-5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          ))}
        </div>
        
        {/* Navigation Controls */}
        {!backgroundOnly && heroes.length > 1 && (
          <>
            {/* Glass Circle Navigation Buttons - Tablet & Desktop */}
            <div className="hidden sm:flex absolute bottom-8 right-8 gap-3 z-40">
              <button
                onClick={scrollPrev}
                className="w-12 h-12 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-all duration-200 hover:scale-105"
                aria-label="Previous hero"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={scrollNext}
                className="w-12 h-12 rounded-full bg-black/60 border border-white/20 flex items-center justify-center text-white hover:bg-black/80 transition-all duration-200 hover:scale-105"
                aria-label="Next hero"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>

            {/* Dot Indicators - All screen sizes */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-40">
              {heroes.map((_, index) => (
                <button 
                  key={index} 
                  onClick={() => scrollTo(index)} 
                  className={`relative overflow-hidden transition-all duration-300 ease-out ${
                    index === currentHeroIndex 
                      ? 'w-8 h-2 bg-white/40' 
                      : 'w-2 h-2 bg-white/50 hover:bg-white/80'
                  } rounded-full`} 
                  aria-label={`Go to hero ${index + 1}`}
                >
                  {index === currentHeroIndex && (
                    <div
                      className="absolute top-0 left-0 h-full bg-white rounded-full transition-all duration-75 ease-linear"
                      style={{ width: `${progress}%` }}
                    />
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
